import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Plus, LayoutGrid, List, MapPin, Truck, Package, Calendar, User, FileText, Send, Loader2, RefreshCw, MoreVertical, Share2, Trash2, DownloadCloud, MessageCircle, Columns3, Image as ImageIcon, Upload, X, ArrowRight, ChevronRight } from 'lucide-react';
import { AdvancedTable } from '../components/AdvancedTable';
import { SupplyFormModal } from '../components/SupplyFormModal';
import { SupplyRecord, SupplyStatus, SUPPLY_STATUS_CONFIG, ProcessStatus, PROCESS_STATUS_CONFIG, getNextProcessStatus, canAdvanceProcess } from '../types/supply';
import { usePermissions } from '../hooks/usePermissions';
import { supplyService } from '../services/supplyService';
import { taskNotificationService } from '../services/taskNotificationService';
import { documentService } from '../services/documentService';
import { vehicleService } from '../services/vehicleService';
import { driverService } from '../services/driverService';
import { bidService } from '../services/bidService';
import { shareTargetService } from '../services/shareTargetService';
import { uiMetadataService } from '../services/uiMetadataService';
import { customerService } from '../services/customerService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { toast } from 'sonner';
import { SUPPLY_METADATA_UPDATED_EVENT } from '../utils/metadataEvents';

type ViewMode = 'grid' | 'cards';
const SUPPLY_VISIBLE_COLUMNS_KEY = 'supplyVisibleColumns';
const viteEnv = ((import.meta as any)?.env || {}) as Record<string, string | undefined>;
const FILE_BASE_URL = (viteEnv.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

type ShipmentDocumentCategory = 'siparis' | 'irsaliye' | 'fatura';

interface ShipmentVisualDocument {
  id: string;
  url: string;
  name: string;
  mimeType?: string;
  uploadedAt?: string;
  source?: 'local' | 'workiom';
}

interface ShipmentDocumentGroups {
  siparis: ShipmentVisualDocument[];
  irsaliye: ShipmentVisualDocument[];
  fatura: ShipmentVisualDocument[];
}

const EMPTY_SHIPMENT_DOCUMENTS: ShipmentDocumentGroups = {
  siparis: [],
  irsaliye: [],
  fatura: [],
};

const resolveFileUrl = (value?: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${FILE_BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

const categorizeShipmentDocument = (documentType: string): ShipmentDocumentCategory | null => {
  const type = String(documentType || '').toLowerCase();
  if (!type) return null;
  if (
    type.includes('irsaliye')
    || type.includes('waybill')
    || type.includes('delivery-proof')
    || type.includes('reference-waybill')
    || type.includes('teslim')
  ) return 'irsaliye';
  if (
    type.includes('fatura')
    || type.includes('invoice')
    || type.includes('e-fatura')
    || type.includes('invoice-document')
  ) return 'fatura';
  if (
    type.includes('siparis')
    || type.includes('order')
    || type.includes('order-form')
    || type.includes('purchase-order')
  ) return 'siparis';
  return null;
};

const categorizeShipmentDocumentFromMeta = (item: any): ShipmentDocumentCategory | null => {
  const direct = categorizeShipmentDocument(item?.document_type || '');
  if (direct) return direct;

  const fileName = String(item?.file_name || item?.name || '').toLowerCase();
  if (fileName.includes('irsaliye') || fileName.includes('waybill') || fileName.includes('teslim')) return 'irsaliye';
  if (fileName.includes('fatura') || fileName.includes('invoice')) return 'fatura';
  if (fileName.includes('siparis') || fileName.includes('order')) return 'siparis';

  return null;
};

const buildShipmentDocumentType = (category: ShipmentDocumentCategory) => {
  if (category === 'siparis') return 'siparis-gorseli';
  if (category === 'irsaliye') return 'irsaliye-gorseli';
  return 'fatura-gorseli';
};

const categorizeByFileName = (fileName: string): ShipmentDocumentCategory | null => {
  const type = String(fileName || '').toLowerCase();
  if (!type) return null;
  if (type.includes('irsaliye') || type.includes('waybill') || type.includes('teslim')) return 'irsaliye';
  if (type.includes('fatura') || type.includes('invoice') || type.includes('efa') || type.includes('efatura')) return 'fatura';
  if (type.includes('siparis') || type.includes('order')) return 'siparis';
  return null;
};

const TURKISH_CITIES = [
  'Adana','Adıyaman','Afyonkarahisar','Ağrı','Aksaray','Amasya','Ankara','Antalya','Ardahan','Artvin','Aydın','Balıkesir','Bartın','Batman','Bayburt','Bilecik','Bingöl','Bitlis','Bolu','Burdur','Bursa','Çanakkale','Çankırı','Çorum','Denizli','Diyarbakır','Düzce','Edirne','Elazığ','Erzincan','Erzurum','Eskişehir','Gaziantep','Giresun','Gümüşhane','Hakkari','Hatay','Iğdır','Isparta','İstanbul','İzmir','Kahramanmaraş','Karabük','Karaman','Kars','Kastamonu','Kayseri','Kırıkkale','Kırklareli','Kırşehir','Kilis','Kocaeli','Konya','Kütahya','Malatya','Manisa','Mardin','Mersin','Muğla','Muş','Nevşehir','Niğde','Ordu','Osmaniye','Rize','Sakarya','Samsun','Siirt','Sinop','Sivas','Şanlıurfa','Şırnak','Tekirdağ','Tokat','Trabzon','Tunceli','Uşak','Van','Yalova','Yozgat','Zonguldak'
];

const normalizeText = (value: string) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9çğıöşü\s]/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const DELIVERY_REGION_BY_CITY: Record<string, string> = {
  istanbul: 'Marmara', bursa: 'Marmara', kocaeli: 'Marmara', sakarya: 'Marmara', tekirdag: 'Marmara', edirne: 'Marmara', kirklareli: 'Marmara', canakkale: 'Marmara', balikesir: 'Marmara', yalova: 'Marmara', bilecik: 'Marmara',
  izmir: 'Ege', manisa: 'Ege', aydin: 'Ege', denizli: 'Ege', mugla: 'Ege', usak: 'Ege', kutahya: 'Ege', afyonkarahisar: 'Ege',
  ankara: 'Ic Anadolu', konya: 'Ic Anadolu', kayseri: 'Ic Anadolu', eskisehir: 'Ic Anadolu', sivas: 'Ic Anadolu', kirikkale: 'Ic Anadolu', aksaray: 'Ic Anadolu', nevsehir: 'Ic Anadolu', nigde: 'Ic Anadolu', karaman: 'Ic Anadolu', kirsehir: 'Ic Anadolu', yozgat: 'Ic Anadolu', cankiri: 'Ic Anadolu',
  antalya: 'Akdeniz', adana: 'Akdeniz', mersin: 'Akdeniz', hatay: 'Akdeniz', osmaniye: 'Akdeniz', kahramanmaras: 'Akdeniz', burdur: 'Akdeniz', isparta: 'Akdeniz',
  samsun: 'Karadeniz', trabzon: 'Karadeniz', ordu: 'Karadeniz', giresun: 'Karadeniz', rize: 'Karadeniz', artvin: 'Karadeniz', gümüşhane: 'Karadeniz', gumushane: 'Karadeniz', tokat: 'Karadeniz', amasya: 'Karadeniz', corum: 'Karadeniz', çorum: 'Karadeniz', sinop: 'Karadeniz', kastamonu: 'Karadeniz', bolu: 'Karadeniz', duzce: 'Karadeniz', zonguldak: 'Karadeniz', bartin: 'Karadeniz', karabuk: 'Karadeniz', bayburt: 'Karadeniz',
  gaziantep: 'Güneydogu Anadolu', sanliurfa: 'Güneydogu Anadolu', şanliurfa: 'Güneydogu Anadolu', diyarbakir: 'Güneydogu Anadolu', mardin: 'Güneydogu Anadolu', batman: 'Güneydogu Anadolu', siirt: 'Güneydogu Anadolu', sirnak: 'Güneydogu Anadolu', şırnak: 'Güneydogu Anadolu', kilis: 'Güneydogu Anadolu', adiyaman: 'Güneydogu Anadolu', adıyaman: 'Güneydogu Anadolu',
  erzurum: 'Dogu Anadolu', malatya: 'Dogu Anadolu', elazig: 'Dogu Anadolu', elazığ: 'Dogu Anadolu', van: 'Dogu Anadolu', mus: 'Dogu Anadolu', muş: 'Dogu Anadolu', bitlis: 'Dogu Anadolu', hakkari: 'Dogu Anadolu', ağrı: 'Dogu Anadolu', agri: 'Dogu Anadolu', igdir: 'Dogu Anadolu', iğdır: 'Dogu Anadolu', kars: 'Dogu Anadolu', ardahan: 'Dogu Anadolu', erzincan: 'Dogu Anadolu', tunceli: 'Dogu Anadolu', bingol: 'Dogu Anadolu', bingöl: 'Dogu Anadolu',
};

const getDeliveryRegion = (city?: string) => DELIVERY_REGION_BY_CITY[normalizeText(city || '')] || '-';

const tokenSimilarity = (a: string, b: string) => {
  const aTokens = new Set(normalizeText(a).split(' ').filter(Boolean));
  const bTokens = new Set(normalizeText(b).split(' ').filter(Boolean));
  if (aTokens.size === 0 || bTokens.size === 0) return 0;
  let overlap = 0;
  for (const token of aTokens) {
    if (bTokens.has(token)) overlap += 1;
  }
  return overlap / Math.max(aTokens.size, bTokens.size);
};

export function SupplyManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { logAction } = useAuditLog();
  const [data, setData] = useState<SupplyRecord[]>([]);
  const [activeBidRequestShipmentIds, setActiveBidRequestShipmentIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SupplyRecord | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('supplyViewMode');
    return (saved as ViewMode) || 'grid';
  });
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [loadingPointFilter, setLoadingPointFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState('');
  const [supplyTab, setSupplyTab] = useState<string>('all');
  const [processItem, setProcessItem] = useState<SupplyRecord | null>(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showQuickWizard, setShowQuickWizard] = useState(false);
  const [detailItem, setDetailItem] = useState<SupplyRecord | null>(null);
  const [detailTab, setDetailTab] = useState<'general' | 'assignment' | 'pricing' | 'documents'>('general');
  const [hammaliyeLoading, setHammaliyeLoading] = useState(false);
  const [quickPrompt, setQuickPrompt] = useState('');
  const [quickInitialData, setQuickInitialData] = useState<any | null>(null);
  const [quickPreview, setQuickPreview] = useState<any | null>(null);
  const [customerLookup, setCustomerLookup] = useState<any[]>([]);
  const [shipmentDocuments, setShipmentDocuments] = useState<Record<string, ShipmentDocumentGroups>>({});
  const [uploadingDocumentKey, setUploadingDocumentKey] = useState<string | null>(null);
  const [imageGallery, setImageGallery] = useState<{
    title: string;
    documents: ShipmentVisualDocument[];
    selectedIndex: number;
  } | null>(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(SUPPLY_VISIBLE_COLUMNS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [tableMetadataColumns, setTableMetadataColumns] = useState<Array<{ key: string; label: string; width?: string; sticky?: boolean; visibleByDefault?: boolean }>>([]);
  const { hasFeaturePermission } = usePermissions();
  const [autoOpenedShipmentId, setAutoOpenedShipmentId] = useState<string | null>(null);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  const vehicleStatusBadge = (value: string) => {
    const normalized = String(value || '').trim().toLocaleUpperCase('tr-TR');
    const styles: Record<string, string> = {
      'YÜK VERİLDİ': 'bg-blue-100 text-blue-700 border-blue-200',
      'ARAÇ BOŞ': 'bg-green-100 text-green-700 border-green-200',
      'ARACIM BOŞ': 'bg-green-100 text-green-700 border-green-200',
      YOLDA: 'bg-amber-100 text-amber-700 border-amber-200',
      'TAKO MOLASI': 'bg-rose-100 text-rose-700 border-rose-200',
      'İZİNDE': 'bg-rose-100 text-rose-700 border-rose-200',
      ARIZALI: 'bg-rose-100 text-rose-700 border-rose-200',
      RAPORLU: 'bg-rose-100 text-rose-700 border-rose-200',
      'YÜKLEME BEKLİYOR': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'YÜKLEMEDE': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'TAHLİYE BEKLİYOR': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    };
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${styles[normalized] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{value || '-'}</span>;
  };

  const processStatusBadge = (value: string) => {
    const normalized = String(value || '').trim().toLocaleLowerCase('tr-TR');
    const styles: Record<string, string> = {
      'işlem bekliyor': 'bg-amber-100 text-amber-700 border-amber-200',
      'islem bekliyor': 'bg-amber-100 text-amber-700 border-amber-200',
      'faturayı işleme al': 'bg-blue-100 text-blue-700 border-blue-200',
      'faturayi isleme al': 'bg-blue-100 text-blue-700 border-blue-200',
      'faturalandırıldı': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'faturalandirildi': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${styles[normalized] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{value || '-'}</span>;
  };

  useEffect(() => {
    localStorage.setItem('supplyViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem(SUPPLY_VISIBLE_COLUMNS_KEY, JSON.stringify(visibleColumnKeys));
  }, [visibleColumnKeys]);

  useEffect(() => {
    loadData();
    loadTableMetadata();
    loadCustomerLookup();
  }, []);

  useAutoRefresh(() => loadData().catch((error) => console.error('Supply auto refresh failed:', error)), { intervalMs: 30000 });

  const loadCustomerLookup = async () => {
    try {
      const response: any = await customerService.getCustomers({ status: 'active' });
      const items = Array.isArray(response?.data) ? response.data : [];
      setCustomerLookup(items);
    } catch (error) {
      console.error('Failed to load customer lookup:', error);
    }
  };

  useEffect(() => {
    const handler = () => {
      void loadTableMetadata();
      setVisibleColumnKeys([]);
    };

    window.addEventListener(SUPPLY_METADATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SUPPLY_METADATA_UPDATED_EVENT, handler);
  }, []);

  const loadTableMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getSupplyTable();
      const cols = Array.isArray(response?.data?.columns) ? response.data.columns : [];
      setTableMetadataColumns(cols);
    } catch (error) {
      console.error('Failed to load supply table metadata:', error);
    }
  };

  const mapDocumentsByShipment = useCallback((documents: any[]): Record<string, ShipmentDocumentGroups> => {
    const grouped: Record<string, ShipmentDocumentGroups> = {};

    for (const item of documents) {
      const shipmentId = String(item?.entity_id || '');
      const category = categorizeShipmentDocumentFromMeta(item);
      const url = resolveFileUrl(item?.file_path || item?.file_url || item?.url);
      const mimeType = String(item?.mime_type || '').toLowerCase();
      const isImage = mimeType.startsWith('image/');
      if (!shipmentId || !category || !url || !isImage) continue;

      if (!grouped[shipmentId]) {
        grouped[shipmentId] = {
          siparis: [],
          irsaliye: [],
          fatura: [],
        };
      }

      grouped[shipmentId][category].push({
        id: String(item.id || `${shipmentId}-${category}-${grouped[shipmentId][category].length}`),
        url,
        name: item.file_name || item.name || 'Gorsel',
        mimeType: item.mime_type,
        uploadedAt: item.uploaded_at,
      });
    }

    return grouped;
  }, []);

  const mergeShipmentDocuments = useCallback((
    localDocuments: ShipmentDocumentGroups,
    workiomAttachments: any[] | undefined,
  ): ShipmentDocumentGroups => {
    const merged: ShipmentDocumentGroups = {
      siparis: [...localDocuments.siparis],
      irsaliye: [...localDocuments.irsaliye],
      fatura: [...localDocuments.fatura],
    };

    for (const attachment of Array.isArray(workiomAttachments) ? workiomAttachments : []) {
      const mimeType = String(attachment?.contentType || '').toLowerCase();
      const fileName = String(attachment?.fileName || attachment?.name || '');
      const fileUrl = attachment?.thumbnailUrl || attachment?.fileUrl || attachment?.url || '';
      const category = categorizeByFileName(fileName);
      if (!category || !mimeType.startsWith('image/') || !fileUrl) continue;

      merged[category].push({
        id: String(attachment?.id || `${category}-${fileName}`),
        url: fileUrl,
        name: fileName || 'Workiom Görsel',
        mimeType,
        source: 'workiom',
      });
    }

    return merged;
  }, []);

  const fetchShipmentDocuments = useCallback(async (shipmentIds: string[]) => {
    if (shipmentIds.length === 0) return {} as Record<string, ShipmentDocumentGroups>;

    const response: any = await documentService.getDocuments({ entityType: 'shipment' });
    const allDocuments = Array.isArray(response?.data) ? response.data : [];
    const shipmentIdSet = new Set(shipmentIds.map((id) => String(id)));
    const relatedDocuments = allDocuments.filter((item: any) => shipmentIdSet.has(String(item?.entity_id || '')));
    return mapDocumentsByShipment(relatedDocuments);
  }, [mapDocumentsByShipment]);

  const fetchShipmentDocumentsById = useCallback(async (shipmentId: string | number) => {
    const response: any = await documentService.getDocuments({
      entityType: 'shipment',
      entityId: String(shipmentId),
    });
    const allDocuments = Array.isArray(response?.data) ? response.data : [];
    const grouped = mapDocumentsByShipment(allDocuments);
    return grouped[String(shipmentId)] || { ...EMPTY_SHIPMENT_DOCUMENTS };
  }, [mapDocumentsByShipment]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await supplyService.getAll();
      
      if (response.success && response.data) {
        const mappedData: SupplyRecord[] = response.data.map((s: any) => ({
          id: s.id,
          sevkKodu: s.sevk_kodu || s.shipment_code || s.id?.toString() || '',
          planKodu: s.plan_kodu || s.plan_code || '',
          yuklemeTalepTarihi: s.yukleme_talep_tarihi || s.request_date || '',
          aracAtamaTarihi: s.arac_atama_tarihi || s.assigned_at || s.assignment_date || '',
          siparisKodu: s.siparis_kodu || s.order_code || '',
          hizliArama: s.hizli_arama || s.quick_search || '',
          durum: mapSupplyStatus(s.status, s.transport_model),
          yuklemeMusterisiAd: s.yukleme_musterisi_ad || s.loading_customer || '',
          yuklemeIl: s.yukleme_il || s.loading_city || '',
          yuklemeIlce: s.yukleme_ilce || s.loading_district || '',
          yuklemeAdres: s.yukleme_adres || s.loading_address || '',
          teslimMusterisiAd: s.teslim_musterisi_ad || s.delivery_customer || '',
          teslimIl: s.teslim_il || s.delivery_city || '',
          teslimIlce: s.teslim_ilce || s.delivery_district || '',
          teslimAdres: s.teslim_adres || s.delivery_address || '',
          aracTipi: s.arac_tipi || s.vehicle_type || '',
          aracTuru: s.arac_turu || s.vehicle_spec_type || '',
          yukTipi: s.yuk_tipi || s.cargo_type || '',
          yukAgirlik: s.yuk_agirlik || s.weight || 0,
          yukHacim: s.yuk_hacim || s.volume || 0,
          palletCount: s.pallet_count || 0,
          vehicleCount: s.vehicle_count || 1,
          stopCount: s.stop_count || 1,
          mesafe: s.mesafe || s.distance || 0,
          seferKm: s.sefer_km || s.distance_km || s.mesafe || s.distance || 0,
          sure: s.sure || s.duration || 0,
          maliyet: s.maliyet || s.cost || 0,
          navlun: s.navlun || s.final_price || 0,
          teklifTlKm: Number((s.distance_km || s.mesafe || 0)) > 0
            ? Number((Number(s.navlun || s.final_price || 0) / Number(s.distance_km || s.mesafe || 1)).toFixed(2))
            : 0,
          kesinFiyat: s.kesin_fiyat || s.fixed_price || 0,
          movementType: s.movement_type || 'warehouse-to-customer',
          pickupWarehouseId: s.pickup_warehouse_id || null,
          deliveryWarehouseId: s.delivery_warehouse_id || null,
          pickupCustomerId: s.pickup_customer_id || null,
          deliveryCustomerId: s.delivery_customer_id || null,
          pickupLatitude: s.pickup_latitude || null,
          pickupLongitude: s.pickup_longitude || null,
          deliveryLatitude: s.delivery_latitude || null,
          deliveryLongitude: s.delivery_longitude || null,
          hedeffiyat: s.hedeffiyat || null,
          shipmentContactPhone: s.shipment_contact_phone || '',
          telefon: s.telefon || s.source_phone || s.shipment_contact_phone || '',
          dorsePlaka: s.dorse_plaka || s.trailer_plate || '',
          cekici: s.cekici_plaka || s.arac_plaka || '',
          talepOlusturan: s.talep_olusturan || s.created_by_name || '',
          operasyonTakipKod: s.operasyon_takip_kod || s.operasyon_takip || '',
          operasyonTakipYuklenecekVardiya: s.operasyon_takip_yuklenecek_vardiya || '',
          faturaBasligi: s.fatura_basligi || '',
          mail: s.mail || s.source_email || '',
          lojistikTuru: s.lojistik_turu || s.logistic_type || '',
          nakliyeciTip: s.nakliyeci_tip || s.carrier_type || '',
          islemDurum: s.islem_durum || s.process_status || 'islem-bekliyor',
          aracDurum: s.arac_durum || s.vehicle_status || '',
          aracAtamasiGerceklestiren: s.arac_atamasi_gerceklestiren || s.assigned_by_name || '',
          komisyon: Number(s.komisyon || 0),
          genelToplam: Number(s.genel_toplam || 0),
          gerceklesenTlKm: Number(s.gerceklesen_tl_km || 0),
          yuklemeIlIlce: s.yukleme_il_ilce || `${s.yukleme_il || s.loading_city || ''}${(s.yukleme_ilce || s.loading_district) ? ` / ${s.yukleme_ilce || s.loading_district}` : ''}`,
          bosaltmaIlIlce: s.bosaltma_il_ilce || `${s.teslim_il || s.delivery_city || ''}${(s.teslim_ilce || s.delivery_district) ? ` / ${s.teslim_ilce || s.delivery_district}` : ''}`,
          surucuBilgileri: s.surucu_bilgileri || s.driver_name || '',
          aciklama: s.aciklama || s.notlar || s.notes || '',
          araNoktalar: s.ara_noktalar || '',
          nearestHubName: s.nearest_hub_name || '',
          nearestHubKm: s.nearest_hub_km != null ? Number(s.nearest_hub_km) : null,
          ugarmaBedeli: Number(s.ugarma_bedeli || 0),
          beklemeBedeli: Number(s.bekleme_bedeli || 0),
          hammaliyeBedeli: Number(s.hammaliye_bedeli || 0),
          yatmaBedeli: Number(s.yatma_bedeli || 0),
          faturaTutari: Number(s.fatura_tutari || 0),
          faturaTarihi: s.fatura_tarihi || '',
          muhasebeIletilmeTarihi: s.muhasebe_iletilme_tarihi || '',
          pickupShiftId: s.pickup_shift_id || null,
          deliveryShiftId: s.delivery_shift_id || null,
          istenenTeslimTarihi: s.istenen_teslim_tarihi || s.delivery_date || '',
          assignedVehicleId: s.assigned_vehicle_id || null,
          createdAt: s.created_at || new Date().toISOString(),
          updatedAt: s.updated_at || new Date().toISOString(),
          workiomRecordId: s.workiom_record_id || '',
        }));

        const [documentMap, bidRequestsResponse] = await Promise.all([
          fetchShipmentDocuments(mappedData.map((item) => String(item.id))),
          bidService.getBidRequests({ status: 'open' }),
        ]);
        setShipmentDocuments(documentMap);
        const bidRequests = Array.isArray((bidRequestsResponse as any)?.data) ? (bidRequestsResponse as any).data : [];
        setActiveBidRequestShipmentIds(bidRequests.map((item: any) => String(item.shipment_id || item.shipmentId || '')).filter(Boolean));

        const mappedWithDocuments = mappedData.map((item) => {
          const docs = mergeShipmentDocuments(
            documentMap[String(item.id)] || EMPTY_SHIPMENT_DOCUMENTS,
            (item as any).workiom_attachments,
          );
          return {
            ...item,
            siparisGorselleri: docs.siparis.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
            irsaliyeGorselleri: docs.irsaliye.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
            faturaGorselleri: docs.fatura.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
          };
        });

        mappedWithDocuments.sort((a, b) => Number(b.sevkKodu || 0) - Number(a.sevkKodu || 0));
        setData(mappedWithDocuments);
      } else {
        toast.error(response.message || 'Veriler yüklenemedi');
        setActiveBidRequestShipmentIds([]);
      }
    } catch (error) {
      console.error('Error loading supply records:', error);
      toast.error('Veriler yüklenirken hata oluştu');
      setActiveBidRequestShipmentIds([]);
    } finally {
      setLoading(false);
    }
  }, [fetchShipmentDocuments, mergeShipmentDocuments]);

  const openImageGallery = useCallback((title: string, documents: ShipmentVisualDocument[], selectedIndex = 0) => {
    if (documents.length === 0) return;
    setImageGallery({
      title,
      documents,
      selectedIndex: Math.max(0, Math.min(selectedIndex, documents.length - 1)),
    });
  }, []);

  const handleUploadShipmentImages = useCallback(async (
    shipmentId: string | number,
    category: ShipmentDocumentCategory,
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) return;

    const allFiles = Array.from(files);
    const imageFiles = allFiles.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== allFiles.length) {
      toast.error('Sadece görsel dosyalar yüklenebilir');
      return;
    }

    const uploadKey = `${shipmentId}-${category}`;
    try {
      setUploadingDocumentKey(uploadKey);

      await Promise.all(
        imageFiles.map((file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entityType', 'shipment');
          formData.append('entityId', String(shipmentId));
          formData.append('documentType', buildShipmentDocumentType(category));
          formData.append('name', file.name);
          return documentService.uploadDocument(formData);
        })
      );

      const refreshedDocuments = await fetchShipmentDocumentsById(shipmentId);

      setShipmentDocuments((prev) => ({
        ...prev,
        [String(shipmentId)]: refreshedDocuments,
      }));

      setData((prev) => prev.map((item) => {
        if (String(item.id) !== String(shipmentId)) return item;
        return {
          ...item,
          siparisGorselleri: refreshedDocuments.siparis.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
          irsaliyeGorselleri: refreshedDocuments.irsaliye.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
          faturaGorselleri: refreshedDocuments.fatura.map((doc) => ({ id: doc.id, url: doc.url, name: doc.name })),
        };
      }));

      toast.success(`${imageFiles.length} görsel yüklendi`);
    } catch (error) {
      console.error('Error uploading shipment visuals:', error);
      toast.error('Görsel yüklenirken hata oluştu');
    } finally {
      setUploadingDocumentKey(null);
    }
  }, [fetchShipmentDocumentsById]);

  const mapSupplyStatus = (status: string, transportModel?: string): SupplyStatus => {
    const normalizedStatus = String(status || '').trim().toLowerCase();
    const normalizedTransportModel = String(transportModel || '').trim().toLowerCase();
    const isSpotModel = ['spot', 'bid', 'external', 'external-driver', 'marketplace'].includes(normalizedTransportModel);
    const isFleetModel = ['own-fleet', 'filo', 'fleet', 'internal', 'internal-driver', 'ozmal', 'özmal'].includes(normalizedTransportModel);

    if (normalizedStatus === 'assigned' && isSpotModel) return 'spot-tedarik-edildi';
    if (normalizedStatus === 'assigned' && isFleetModel) return 'filo-tedarik-edildi';

    const statusMap: Record<string, SupplyStatus> = {
      'pending': 'tedarik-bekleniyor',
      'waiting': 'tedarik-bekleniyor',
      'supply-requested': 'tedarik-bekleniyor',
      'supplying': 'tedarik-surecinde',
      'in-progress': 'tedarik-surecinde',
      'filo-tedarik-edildi': 'filo-tedarik-edildi',
      'spot': 'spot-tedarik-edildi',
      'spot-tedarik-edildi': 'spot-tedarik-edildi',
      'open': 'spot-teklif-bekleniyor',
      'teklif-topla': 'spot-teklif-bekleniyor',
      'teklif topla': 'spot-teklif-bekleniyor',
      'spot-teklif-bekleniyor': 'spot-teklif-bekleniyor',
      'completed': 'tedarik-tamamlandi',
      'delivered': 'tedarik-tamamlandi',
      'faturalandirildi': 'faturalandirildi',
      'cancelled': 'tedarik-iptal',
      'canceled': 'tedarik-iptal',
      'iptal': 'tedarik-iptal',
    };
    return statusMap[normalizedStatus] || (isSpotModel ? 'spot-teklif-bekleniyor' : 'tedarik-bekleniyor');
  };

  const StatusBadge = ({ status }: { status: SupplyStatus }) => {
    const config = SUPPLY_STATUS_CONFIG[status];
    if (!config) {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700">
          {status || 'Bilinmeyen'}
        </span>
      );
    }
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
        style={{ backgroundColor: config.bgColor, color: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const renderShipmentDocumentCell = (
    record: SupplyRecord,
    category: ShipmentDocumentCategory,
    label: string,
  ) => {
    const docs = shipmentDocuments[String(record.id)]?.[category] || [];
    const uploadKey = `${record.id}-${category}`;
    const isUploading = uploadingDocumentKey === uploadKey;
    const inputId = `shipment-upload-${category}-${record.id}`;

    return (
      <div className="flex items-center gap-2 min-w-[190px]">
        <div className="flex items-center gap-1">
          {docs.length > 0 ? (
            <>
              {docs.slice(0, 3).map((doc, index) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => openImageGallery(`${label} - #${record.sevkKodu || record.id}`, docs, index)}
                  className="w-8 h-8 rounded-md overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors bg-gray-100"
                  title={doc.name}
                >
                  <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
              {docs.length > 3 && (
                <button
                  type="button"
                  onClick={() => openImageGallery(`${label} - #${record.sevkKodu || record.id}`, docs, 0)}
                  className="w-8 h-8 rounded-md border border-gray-300 text-[10px] font-semibold text-gray-700 bg-white hover:bg-gray-50"
                >
                  +{docs.length - 3}
                </button>
              )}
            </>
          ) : (
            <span className="text-gray-400 text-xs">Yok</span>
          )}
        </div>

        <input
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            void handleUploadShipmentImages(record.id, category, event.target.files);
            event.currentTarget.value = '';
          }}
        />
        <label
          htmlFor={inputId}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
          title={`${label} yükle`}
        >
          {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        </label>
      </div>
    );
  };

  const allColumns = useMemo(() => [
    {
      key: 'sevkKodu' as keyof SupplyRecord,
      label: 'Sevk Kodu',
      width: '140px',
      sortable: true,
      filterable: true,
    },
    {
      key: 'planKodu' as keyof SupplyRecord,
      label: 'Plan Kodu',
      width: '140px',
      sortable: true,
      filterable: true,
    },
    {
      key: 'aracAtamaTarihi' as keyof SupplyRecord,
      label: 'Araç Atama Tarihi',
      width: '180px',
      sortable: true,
      filterable: true,
      render: (value: string) => value ? new Date(value).toLocaleString('tr-TR') : '-',
    },
    {
      key: 'siparisKodu' as keyof SupplyRecord,
      label: 'Sipariş No',
      width: '140px',
      sortable: true,
      filterable: true,
    },
    {
      key: 'yuklemeMusterisiAd' as keyof SupplyRecord,
      label: 'Yükleme Noktası',
      width: '220px',
      sortable: true,
      filterable: true,
    },
    {
      key: 'teslimMusterisiAd' as keyof SupplyRecord,
      label: 'Teslim Noktaları',
      width: '280px',
      sortable: true,
      filterable: true,
      render: (_: any, record: SupplyRecord) => {
        const extraStops = String(record.araNoktalar || '').trim();
        if (!extraStops || extraStops === '-') return record.teslimMusterisiAd || '-';
        return `${record.teslimMusterisiAd || '-'} | ${extraStops}`;
      },
    },
    {
      key: 'yuklemeIl' as keyof SupplyRecord,
      label: 'Yükleme İl/İlçe',
      width: '160px',
      sortable: true,
      filterable: true,
      render: (_: any, record: SupplyRecord) => `${record.yuklemeIl || '-'} / ${record.yuklemeIlce || '-'}`,
    },
    {
      key: 'teslimIl' as keyof SupplyRecord,
      label: 'Teslim İl/İlçe',
      width: '160px',
      sortable: true,
      filterable: true,
      render: (_: any, record: SupplyRecord) => `${record.teslimIl || '-'} / ${record.teslimIlce || '-'}`,
    },
    {
      key: 'nearestHubName' as keyof SupplyRecord,
      label: 'Dönüş Hub',
      width: '220px',
      sortable: true,
      filterable: true,
      render: (_: any, record: SupplyRecord) => record.nearestHubName ? `${record.nearestHubName}${record.nearestHubKm != null ? ` (${Number(record.nearestHubKm).toFixed(1)} km)` : ''}` : '-',
    },
    {
      key: 'teslimIl' as keyof SupplyRecord,
      label: 'Teslim Bölgesi',
      width: '140px',
      sortable: true,
      filterable: true,
      render: (_: any, record: SupplyRecord) => getDeliveryRegion(record.teslimIl),
    },
    { key: 'yuklemeIlce' as keyof SupplyRecord, label: 'Yükleme İlçesi', width: '150px', sortable: true, filterable: true },
    { key: 'teslimIlce' as keyof SupplyRecord, label: 'Boşaltma İlçesi', width: '150px', sortable: true, filterable: true },
    { key: 'operasyonTakipKod' as keyof SupplyRecord, label: 'Operasyon Takip', width: '180px', sortable: true, filterable: true },
    { key: 'talepOlusturan' as keyof SupplyRecord, label: 'Talep Oluşturan', width: '180px', sortable: true, filterable: true },
    {
      key: 'aciklama' as keyof SupplyRecord,
      label: 'Notlar',
      width: '280px',
      sortable: false,
      filterable: true,
      render: (_: any, record: SupplyRecord) => {
        const isEditing = editingNotesId === String(record.id);
        if (isEditing) {
          return (
            <textarea
              autoFocus
              value={editingNotesValue}
              onChange={(e) => setEditingNotesValue(e.target.value)}
              onBlur={() => handleNotesSave(record, editingNotesValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  (e.target as HTMLTextAreaElement).blur();
                }
                if (e.key === 'Escape') {
                  setEditingNotesId(null);
                  setEditingNotesValue('');
                }
              }}
              className="w-full rounded border border-blue-400 px-2 py-1 text-xs outline-none resize-none"
              rows={2}
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <div
            className="group flex items-center gap-1 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setEditingNotesId(String(record.id));
              setEditingNotesValue(record.aciklama || '');
            }}
          >
            <span className="text-xs text-slate-600 line-clamp-2 min-w-0 flex-1">
              {record.aciklama || '-'}
            </span>
            <FileText className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0" />
          </div>
        );
      },
    },
    {
      key: 'yuklemeTalepTarihi' as keyof SupplyRecord,
      label: 'Transfer Tarihi',
      width: '180px',
      sortable: true,
      filterable: true,
      render: (value: string) => value ? new Date(value).toLocaleString('tr-TR') : '-',
    },
    {
      key: 'maliyet' as keyof SupplyRecord,
      label: 'Baz Fiyat',
      width: '140px',
      sortable: true,
      filterable: true,
      render: (value: number) => Number(value || 0) > 0 ? (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 border border-emerald-200">
          Baz Fiyat: ₺{Number(value).toLocaleString('tr-TR')}
        </span>
      ) : '-',
    },
    {
      key: 'hedeffiyat' as keyof SupplyRecord,
      label: 'Kesin Fiyat',
      width: '180px',
      sortable: true,
      filterable: true,
      render: (value: string) => value ? new Date(value).toLocaleString('tr-TR') : '-',
    },
    {
      key: 'createdAt' as keyof SupplyRecord,
      label: 'Teslim Tarihi',
      width: '180px',
      sortable: true,
      filterable: true,
      render: (_: any, record: any) => record.istenenTeslimTarihi ? new Date(record.istenenTeslimTarihi).toLocaleString('tr-TR') : '-',
    },
    {
      key: 'aracTipi' as keyof SupplyRecord,
      label: 'Araç Tipi',
      width: '120px',
      sortable: true,
      filterable: true,
    },
    { key: 'yukTipi' as keyof SupplyRecord, label: 'Yük Tipi', width: '140px', sortable: true, filterable: true },
    { key: 'yukAgirlik' as keyof SupplyRecord, label: 'Ağırlık (kg)', width: '120px', sortable: true, filterable: true },
    { key: 'yukHacim' as keyof SupplyRecord, label: 'Hacim (m³)', width: '120px', sortable: true, filterable: true },
    { key: 'palletCount' as keyof SupplyRecord, label: 'Palet', width: '90px', sortable: true, filterable: true },
    { key: 'vehicleCount' as keyof SupplyRecord, label: 'Araç Sayısı', width: '100px', sortable: true, filterable: true },
    { key: 'stopCount' as keyof SupplyRecord, label: 'Uğrama', width: '90px', sortable: true, filterable: true },
    { key: 'telefon' as keyof SupplyRecord, label: 'Telefon', width: '150px', sortable: true, filterable: true },
    { key: 'aracDurum' as keyof SupplyRecord, label: 'Araç Durum', width: '170px', sortable: true, filterable: true, render: (value: string) => vehicleStatusBadge(value) },
    { key: 'islemDurum' as keyof SupplyRecord, label: 'İşlem Durum', width: '180px', sortable: true, filterable: true, render: (value: string) => processStatusBadge(value) },
    { key: 'surucuBilgileri' as keyof SupplyRecord, label: 'Sürücü Bilgileri', width: '200px', sortable: true, filterable: true },
    { key: 'cekici' as keyof SupplyRecord, label: 'Çekici', width: '130px', sortable: true, filterable: true },
    { key: 'dorsePlaka' as keyof SupplyRecord, label: 'Dorse', width: '130px', sortable: true, filterable: true },
    { key: 'seferKm' as keyof SupplyRecord, label: 'Sefer Km', width: '110px', sortable: true, filterable: true },
    { key: 'navlun' as keyof SupplyRecord, label: 'Navlun', width: '130px', sortable: true, filterable: true },
    { key: 'teklifTlKm' as keyof SupplyRecord, label: 'Teklif TL/Km', width: '130px', sortable: true, filterable: true },
    { key: 'komisyon' as keyof SupplyRecord, label: 'Komisyon', width: '120px', sortable: true, filterable: true },
    { key: 'genelToplam' as keyof SupplyRecord, label: 'Genel Toplam', width: '130px', sortable: true, filterable: true },
    { key: 'gerceklesenTlKm' as keyof SupplyRecord, label: 'Gerçekleşen TL/Km', width: '150px', sortable: true, filterable: true },
    { key: 'mesafe' as keyof SupplyRecord, label: 'Mesafe (km)', width: '110px', sortable: true, filterable: true },
    { key: 'sure' as keyof SupplyRecord, label: 'Süre (saat)', width: '110px', sortable: true, filterable: true },
    {
      key: 'siparisGorselleri' as keyof SupplyRecord,
      label: 'Sipariş Görseli',
      width: '220px',
      sortable: false,
      filterable: false,
      render: (_: any, record: SupplyRecord) => renderShipmentDocumentCell(record, 'siparis', 'Sipariş Görselleri'),
    },
    {
      key: 'irsaliyeGorselleri' as keyof SupplyRecord,
      label: 'İrsaliye Görseli',
      width: '220px',
      sortable: false,
      filterable: false,
      render: (_: any, record: SupplyRecord) => renderShipmentDocumentCell(record, 'irsaliye', 'İrsaliye Görselleri'),
    },
    {
      key: 'faturaGorselleri' as keyof SupplyRecord,
      label: 'Fatura Görseli',
      width: '220px',
      sortable: false,
      filterable: false,
      render: (_: any, record: SupplyRecord) => renderShipmentDocumentCell(record, 'fatura', 'Fatura Görselleri'),
    },
    {
      key: 'durum' as keyof SupplyRecord,
      label: 'Durum',
      width: '150px',
      sortable: true,
      filterable: true,
      render: (value: SupplyStatus) => <StatusBadge status={value} />,
    },
    {
      key: 'id' as keyof SupplyRecord,
      label: 'Aksiyonlar',
      width: '260px',
      sortable: false,
      filterable: false,
      render: (value: any, record: SupplyRecord) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingItem(record);
                setIsFormOpen(true);
              }}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
            >
              Düzenle
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm(`#${record.sevkKodu} kaydını silmek istediğinize emin misiniz?`)) return;
                try {
                  await supplyService.delete(record.id);
                  toast.success('Kayıt silindi');
                  await loadData();
                } catch (error) {
                  console.error('Error deleting supply record:', error);
                  toast.error('Silme hatası');
                }
              }}
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
            >
              Sil
            </button>
            {(record.durum === 'tedarik-bekleniyor' || record.durum === 'tedarik-bekliyor') && !activeBidRequestShipmentIds.includes(String(record.id)) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateOfferRequest(record);
                }}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
              >
                <Send className="w-3 h-3" />
                Teklif Topla
              </button>
            )}
          </div>
        );
      },
    },
  ], [openImageGallery, shipmentDocuments, uploadingDocumentKey, activeBidRequestShipmentIds]);

  const metadataDrivenColumns = useMemo(() => {
    if (tableMetadataColumns.length === 0) return allColumns;
    const map = new Map(allColumns.map((column) => [String(column.key), column]));
    const merged = tableMetadataColumns
      .map((meta) => {
        const existing = map.get(meta.key);
        if (!existing) return null;
        return {
          ...existing,
          label: meta.label || existing.label,
          width: meta.width || existing.width,
        };
      })
      .filter(Boolean) as typeof allColumns;

    const extra = allColumns.filter((column) => !tableMetadataColumns.some((meta) => meta.key === String(column.key)));
    return [...merged, ...extra];
  }, [allColumns, tableMetadataColumns]);

  useEffect(() => {
    if (visibleColumnKeys.length === 0 && metadataDrivenColumns.length > 0) {
      const defaults = tableMetadataColumns.length > 0
        ? tableMetadataColumns.filter((column) => column.visibleByDefault !== false).map((column) => column.key)
        : metadataDrivenColumns.filter((column) => column.key !== 'id').map((column) => String(column.key));
      setVisibleColumnKeys(defaults);
    }
  }, [metadataDrivenColumns, tableMetadataColumns, visibleColumnKeys.length]);

  const columns = useMemo(
    () => metadataDrivenColumns.filter((column) => column.key === 'id' || visibleColumnKeys.includes(String(column.key))),
    [metadataDrivenColumns, visibleColumnKeys]
  );

  const handleCreateOfferRequest = async (record: SupplyRecord) => {
    if (activeBidRequestShipmentIds.includes(String(record.id)) || record.durum === 'spot-teklif-bekleniyor') {
      toast.error('Bu sevkiyat zaten teklif toplama sürecinde');
      return;
    }
    if (!window.confirm(`#${record.sevkKodu} sevkiyatı için teklif toplamak istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setSaving(true);
      
      const bidRequestResponse: any = await bidService.createBidRequest({
        shipmentId: record.id,
        loadingPoint: record.yuklemeMusterisiAd,
        loadingCity: record.yuklemeIl,
        loadingAddress: record.yuklemeAdres,
        deliveryPoint: record.teslimMusterisiAd,
        deliveryCity: record.teslimIl,
        deliveryAddress: record.teslimAdres,
        cargoType: record.yukTipi,
        cargoWeight: record.yukAgirlik,
        cargoVolume: record.yukHacim,
        distanceKm: record.mesafe,
        vehicleType: record.aracTipi,
        targetPrice: record.targetPrice,
        plannedLoadingTime: record.yuklemeTalepTarihi,
        plannedDeliveryTime: (record as any).istenenTeslimTarihi || null,
        bidDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        minimumBid: record.maliyet || 0,
        requireReferenceWaybill: Boolean((record as any).requireReferenceWaybill || (record as any).require_reference_waybill),
        phoneOnlyQuote: Boolean((record as any).phoneOnlyQuote || (record as any).phone_only_quote),
        shipmentContactPhone: (record as any).shipmentContactPhone || (record as any).shipment_contact_phone || '',
        notes: record.notlar,
        createdBy: 1,
      });

      const bidRequestId = bidRequestResponse?.data?.id || bidRequestResponse?.id;
      if (bidRequestId) {
        const shareLines = [
          `Yeni taşıma fırsatı: ${record.sevkKodu}`,
          `Yükleme noktası: ${record.yuklemeMusterisiAd} / ${record.yuklemeIl}`,
          `Teslim noktası: ${record.teslimMusterisiAd} / ${record.teslimIl}`,
          `Araç tipi: ${record.aracTipi}`,
          `Yük ağırlığı: ${Number(record.yukAgirlik || 0).toLocaleString('tr-TR')} kg`,
          `Tahmini mesafe: ${record.mesafe} km`,
          '',
          `Detaylar için sürücü uygulamasını kullanın: ${window.location.origin}/driver-mobile-auth`,
        ];
        await shareTargetService.queueBidRequestShares(bidRequestId, shareLines.join('\n'));
      }

      const response = await supplyService.updateStatus(record.id, 'spot-teklif-bekleniyor');
      
      if (response.success) {
        await taskNotificationService.notifyOfferCollection({
          loadingCustomer: record.yuklemeMusterisiAd,
          loadingCity: record.yuklemeIl,
          deliveryCity: record.teslimIl,
          tonnage: record.yukAgirlik,
        }).catch((error) => console.error('Offer collection notification failed:', error));
        toast.success('Teklif talebi oluşturuldu');
        await loadData();
        
        window.dispatchEvent(new CustomEvent('notificationUpdate', {
          detail: {
            type: 'info',
            title: 'Teklif Talebi Oluşturuldu',
            message: `#${record.sevkKodu} sevkiyatı için teklif talebi oluşturuldu.`,
          }
        }));
      } else {
        toast.error(response.message || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error creating offer request:', error);
      toast.error('İşlem hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (newItem: SupplyRecord) => {
    try {
      setSaving(true);
      
      let response;
      if (editingItem) {
        response = await supplyService.update(editingItem.id, newItem);
        if (response.success) {
          toast.success('Kayıt güncellendi');
          await loadData();
        }
      } else {
        response = await supplyService.create(newItem);
        if (response.success && response.data) {
          toast.success('Kayıt oluşturuldu');
          await loadData();
        }
      }
      
      if (!response?.success) {
        toast.error(response?.message || 'İşlem başarısız');
      }
      
      setIsFormOpen(false);
      setQuickInitialData(null);
    } catch (error) {
      console.error('Error saving supply record:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleNotesSave = async (record: SupplyRecord, newValue: string) => {
    try {
      const trimmed = newValue.trim();
      const response = await supplyService.update(record.id, { notlar: trimmed });
      if (response.success) {
        setData((prev) => prev.map((r) => (r.id === record.id ? { ...r, aciklama: trimmed } : r)));
      } else {
        toast.error(response?.message || 'Not güncellenemedi');
      }
    } catch {
      toast.error('Not güncellenirken hata oluştu');
    } finally {
      setEditingNotesId(null);
      setEditingNotesValue('');
    }
  };

  const filteredSupplyData = useMemo(() => {
    const toLocalDateString = (value?: string) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const normalizedFilter = loadingPointFilter.toLocaleLowerCase('tr-TR');

    return data.filter((item) => {
      const loadingText = `${item.yuklemeMusterisiAd || ''} ${item.yuklemeIl || ''} ${item.yuklemeIlce || ''}`;
      const loadingMatches = !normalizedFilter || loadingText.toLocaleLowerCase('tr-TR').includes(normalizedFilter);
      const dateMatches = !dateFilter || toLocalDateString(item.yuklemeTalepTarihi) === dateFilter;
      const vehicleStatusMatches = !vehicleStatusFilter || String(item.aracDurum || '').trim() === vehicleStatusFilter;
      
      let tabMatches = true;
      if (supplyTab !== 'all') {
        const status = item.durum;
        const hasAssignedDriver = Boolean(String(item.surucuBilgileri || item.surucuAd || '').trim());
        const hasAssignedVehicle = Boolean(String(item.cekici || item.aracPlaka || '').trim());
        const isUnassignedSupply = !hasAssignedDriver && !hasAssignedVehicle;
        
        switch (supplyTab) {
          case 'tedarik-bekliyor':
            tabMatches = isUnassignedSupply || status === 'tedarik-bekleniyor' || status === 'tedarik-bekliyor';
            break;
          case 'teklif-bekliyor':
            tabMatches = activeBidRequestShipmentIds.includes(String(item.id));
            break;
          case 'filo-tedarik':
            tabMatches = status === 'filo-tedarik-edildi';
            break;
          case 'spot-tedarik':
            tabMatches = status === 'spot-tedarik-edildi';
            break;
          default:
            tabMatches = true;
        }
      }
      
      return loadingMatches && dateMatches && vehicleStatusMatches && tabMatches;
    });
  }, [data, loadingPointFilter, dateFilter, vehicleStatusFilter, supplyTab, activeBidRequestShipmentIds]);

  useEffect(() => {
    const shipmentId = searchParams.get('shipment');
    if (!shipmentId || loading) return;
    if (autoOpenedShipmentId === shipmentId) return;

    const matched = data.find((item) => String(item.id) === String(shipmentId));
    if (matched) {
      setEditingItem(matched);
      setIsFormOpen(true);
      setAutoOpenedShipmentId(shipmentId);
    }
  }, [searchParams, data, loading, autoOpenedShipmentId]);

  const findBestCustomerMatch = (text: string) => {
    const normalizedText = normalizeText(text);
    let best: any = null;
    let bestScore = 0;

    for (const customer of customerLookup) {
      const name = String(customer.company_name || customer.name || '');
      if (!name) continue;
      const normalizedName = normalizeText(name);

      let score = 0;
      if (normalizedText.includes(normalizedName)) score += 2;
      score += tokenSimilarity(normalizedText, normalizedName) * 5;

      const city = String(customer.city || '');
      const district = String(customer.district || '');
      if (city && normalizedText.includes(normalizeText(city))) score += 0.5;
      if (district && normalizedText.includes(normalizeText(district))) score += 0.5;

      if (score > bestScore) {
        best = customer;
        bestScore = score;
      }
    }

    return bestScore >= 1.5 ? best : null;
  };

  const extractCityDistrict = (text: string) => {
    const normalized = normalizeText(text);
    const city = TURKISH_CITIES.find((item) => normalized.includes(normalizeText(item)));
    if (!city) return { city: '', district: '' };

    const cityIndex = normalized.indexOf(normalizeText(city));
    const afterCity = normalized.slice(cityIndex + normalizeText(city).length).trim();
    const district = afterCity.split(' ')[0] || '';

    return { city, district: district ? district.charAt(0).toUpperCase() + district.slice(1) : '' };
  };

  const extractStops = (text: string, keyword: 'yükleme' | 'bosaltma') => {
    const normalizedLines = text.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    const keywordAliases = keyword === 'yükleme' ? ['yükleme', 'yukleme'] : ['boşaltma', 'bosaltma', 'teslim'];
    return normalizedLines.filter((line) => keywordAliases.some((alias) => normalizeText(line).includes(alias)));
  };

  const parseQuickSupplyPrompt = (text: string) => {
    const raw = text.trim();
    const lines = raw.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    const joined = lines.join(' ');
    const pickupLines = extractStops(raw, 'yükleme');
    const deliveryLines = extractStops(raw, 'bosaltma');
    const pickupMatch = joined.match(/yükleme[:\s-]+([^,\n]+?)(?:\s*[,/-]\s*|\s+)([A-Za-zÇĞİÖŞÜçğıöşü]+)(?:\s+([A-Za-zÇĞİÖŞÜçğıöşü]+))?/i);
    const deliveryMatch = joined.match(/boşaltma[:\s-]+([^,\n]+?)(?:\s*[,/-]\s*|\s+)([A-Za-zÇĞİÖŞÜçğıöşü]+)(?:\s+([A-Za-zÇĞİÖŞÜçğıöşü]+))?/i);
    const plateMatch = joined.match(/araç tür[üu][:\s-]+([^,\n]+)/i);
    const orderMatch = joined.match(/(?:sipariş|order)[:\s-]+([A-Za-z0-9-]+)/i);
    const weightMatch = joined.match(/(?:tonaj|kg|ağırlık)[:\s-]+([0-9.,]+)/i);
    const palletMatch = joined.match(/(?:palet)[:\s-]+([0-9]+)/i);
    const navlunMatch = joined.match(/(?:navlun|fiyat)[:\s-]+([0-9.]+)/i);
    const phoneMatch = joined.match(/(?:telefon|tel)[:\s-]+(\+?\d[\d\s-]+)/i);
    const stopMatch = joined.match(/(?:durak|uğrama|ugrama)[:\s-]+([0-9]+)/i);
    const loadTypeMatch = joined.match(/(?:yük türü|yük tipi|urun|ürün)[:\s-]+([^,\n]+)/i);
    const pickupDateMatch = joined.match(/(?:yükleme tarihi|yükleme talep tarihi|tarih)[:\s-]+([0-9]{1,2}[./-][0-9]{1,2}[./-][0-9]{2,4})/i);

    const pickupSeed = pickupLines[0] || `${pickupMatch?.[1] || ''} ${joined}`;
    const deliverySeed = deliveryLines[0] || `${deliveryMatch?.[1] || ''}`;
    const pickupCustomer = findBestCustomerMatch(pickupSeed);
    const deliveryCustomer = findBestCustomerMatch(deliverySeed);
    const pickupLocation = extractCityDistrict(pickupSeed);
    const deliveryLocation = extractCityDistrict(deliverySeed);

    const parseDate = (value?: string) => {
      if (!value) return '';
      const parts = value.split(/[./-]/).map((p) => p.trim());
      if (parts.length !== 3) return '';
      const [day, month, year] = parts;
      const yyyy = year.length === 2 ? `20${year}` : year;
      return `${yyyy}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T09:00`;
    };

    return {
      sevkKodu: '',
      siparisKodu: orderMatch?.[1] || '',
      yuklemeMusterisiAd: pickupCustomer?.company_name || pickupMatch?.[1] || '',
      yuklemeIl: pickupCustomer?.city || pickupLocation.city || pickupMatch?.[2] || '',
      yuklemeIlce: pickupCustomer?.district || pickupLocation.district || pickupMatch?.[3] || '',
      yuklemeAdres: pickupCustomer?.address || '',
      teslimMusterisiAd: deliveryCustomer?.company_name || deliveryMatch?.[1] || '',
      teslimIl: deliveryCustomer?.city || deliveryLocation.city || deliveryMatch?.[2] || '',
      teslimIlce: deliveryCustomer?.district || deliveryLocation.district || deliveryMatch?.[3] || '',
      aracTuru: plateMatch?.[1] || '',
      yukTipi: loadTypeMatch?.[1] || '',
      yuklemeTalepTarihi: parseDate(pickupDateMatch?.[1]),
      yukAgirlik: weightMatch?.[1] ? Number(String(weightMatch[1]).replace(',', '.')) : 0,
      palletCount: palletMatch?.[1] ? Number(palletMatch[1]) : 0,
      navlun: navlunMatch?.[1] ? Number(navlunMatch[1]) : 0,
      telefon: phoneMatch?.[1] || '',
      stopCount: stopMatch?.[1] ? Number(stopMatch[1]) : 1,
      cokluBosaltmaHedefleri: deliveryLines,
      aciklama: raw,
      notlar: raw,
      durum: 'tedarik-bekleniyor',
      vehicleCount: 1,
    };
  };

  const handleQuickWizardApply = () => {
    const parsed = parseQuickSupplyPrompt(quickPrompt);
    setQuickPreview(parsed);
    setQuickInitialData(parsed);
    setEditingItem(null);
    setShowQuickWizard(false);
    setIsFormOpen(true);
  };

  const handleQuickWizardPreview = () => {
    setQuickPreview(parseQuickSupplyPrompt(quickPrompt));
  };

  const stats = useMemo(() => {
    let waiting = 0;
    let inProgress = 0;
    let completed = 0;

    for (const record of data) {
      const hasAssignedDriver = Boolean(String(record.surucuBilgileri || record.surucuAd || '').trim());
      const hasAssignedVehicle = Boolean(String(record.cekici || record.aracPlaka || '').trim());
      const isUnassignedSupply = !hasAssignedDriver && !hasAssignedVehicle;

      if (record.durum === 'tedarik-bekleniyor' || record.durum === 'tedarik-bekliyor' || record.durum === 'spot-teklif-bekleniyor' || isUnassignedSupply) waiting += 1;
      else if (record.durum === 'tedarik-surecinde') inProgress += 1;
      else if (record.durum === 'tedarik-tamamlandi') completed += 1;
    }

    return {
      total: data.length,
      waiting,
      inProgress,
      completed,
    };
  }, [data]);

  const activeTabLabel = useMemo(() => {
    const labels: Record<string, string> = {
      all: 'Tüm Kayıtlar',
      'tedarik-bekliyor': 'Tedarik Bekleyenler',
      'teklif-bekliyor': 'Spot Teklif Bekleyenler',
      'filo-tedarik': 'Filo Tedarik Edilenler',
      'spot-tedarik': 'Spot Tedarik Edilenler',
    };
    return labels[supplyTab] || 'Tüm Kayıtlar';
  }, [supplyTab]);

  const selectedRecords = filteredSupplyData.filter((item) => selectedItems.includes(String(item.id)));

  const driverPortalUrl = (viteEnv.VITE_DRIVER_PORTAL_URL || `${window.location.origin}/driver-mobile-auth`).trim();

  const buildBulkShareMessage = (records: SupplyRecord[]) => {
    const grouped = records.reduce((acc, record) => {
      const key = `${record.yuklemeMusterisiAd || '-'}|${record.yuklemeIl || '-'}|${String(record.yuklemeTalepTarihi || '').slice(0, 10)}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(record);
      return acc;
    }, {} as Record<string, SupplyRecord[]>);

    const sections = Object.values(grouped).map((group) => {
      const first = group[0];
      return [
        '🚛 YÜKLEME LİSTESİ',
        '',
        `📌 Yükleme İli: ${String(first.yuklemeIl || '-').toUpperCase()}`,
        `🏗️ Müşteri: ${first.yuklemeMusterisiAd || '-'}`,
        '',
        ...group.map((record) => `📍 Boşaltma: ${record.teslimIl || '-'}${record.teslimIlce ? ` ${record.teslimIlce}` : ''} ⚖️ Tonaj: ${record.yukAgirlik || 0}`),
      ].join('\n');
    });

    return [
      'Yük Ortağım',
      'Nakliye Teklif Portalı',
      driverPortalUrl.replace(/^https?:\/\//, ''),
      '',
      ...sections,
      '',
      'Yük teklifi vermek için mobil uygulamamıza giriniz:',
      `👉 ${driverPortalUrl}`,
      '',
      '📍 YASAL UYARI',
      'Bu sevkiyat ve iş emri hakları münhasıran DOĞUŞ ÇAY’a aittir. Söz konusu ilanın izinsiz paylaşılması, "iletildi" yapılarak yayılması veya fiyat üzerinde değişiklik yapılarak üçüncü kişilerle paylaşılması yasaktır.',
    ].join('\n');
  };

  const handleBulkOfferRequest = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }

    const eligibleRecords = selectedRecords.filter((record) => !activeBidRequestShipmentIds.includes(String(record.id)) && record.durum !== 'spot-teklif-bekleniyor');
    if (eligibleRecords.length === 0) {
      toast.error('Seçilen kayıtlar zaten teklif toplama sürecinde');
      return;
    }

    setShowBulkMenu(false);
    for (const record of eligibleRecords) {
      await handleCreateOfferRequest(record);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }
    if (!window.confirm(`${selectedRecords.length} kaydı silmek istediğinize emin misiniz?`)) return;

    try {
      setSaving(true);
      for (const record of selectedRecords) {
        await supplyService.delete(record.id);
      }
      toast.success(`${selectedRecords.length} kayıt silindi`);
      setSelectedItems([]);
      setShowBulkMenu(false);
      await loadData();
    } catch (error) {
      console.error('Error bulk deleting supply records:', error);
      toast.error('Toplu silme sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkShare = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }

    try {
      const targetsResponse: any = await shareTargetService.getTargets();
      const targets = Array.isArray(targetsResponse?.data) ? targetsResponse.data : [];
      const combinedText = buildBulkShareMessage(selectedRecords);
      await navigator.clipboard.writeText(combinedText);
      let openedCount = 0;
      targets.forEach((target: any) => {
        if (target.platform === 'whatsapp' && target.phone_number) {
          openedCount += 1;
          window.open(`https://wa.me/${String(target.phone_number).replace(/\D/g, '')}?text=${encodeURIComponent(combinedText)}`, '_blank', 'noopener,noreferrer');
        }
        if (target.platform === 'facebook' && target.target_url) {
          openedCount += 1;
          window.open(target.target_url, '_blank', 'noopener,noreferrer');
        }
      });
      toast.success(`${selectedRecords.length} kayıt için paylaşım metni hazırlandı. ${openedCount} hedef açıldı.`);
      setShowBulkMenu(false);
    } catch (error) {
      console.error('Error bulk sharing supply records:', error);
      toast.error('Toplu paylaşım sırasında hata oluştu');
    }
  };

  const handleBulkWhatsappShare = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Lütfen en az bir kayıt seçin');
      return;
    }

    try {
      const whatsappMessage = buildBulkShareMessage(selectedRecords);
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
      const popup = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        throw new Error('WhatsApp penceresi engellendi');
      }

      try {
        await navigator.clipboard.writeText(whatsappMessage);
      } catch (clipboardError) {
        console.warn('Clipboard copy failed:', clipboardError);
      }

      toast.success('WhatsApp paylaşımı açıldı ve metin panoya kopyalandı');
      setShowBulkMenu(false);
    } catch (error) {
      console.error('Error opening WhatsApp share:', error);
      toast.error('WhatsApp paylaşımı açılamadı. Popup engelini kontrol edin.');
    }
  };

  const handleAdvanceProcess = async (item: SupplyRecord) => {
    try {
      setSaving(true);
      const response = await supplyService.advanceProcessStatus(item.id);
      if (response.success) {
        toast.success(response.message || 'Süreç ilerletildi');
        await loadData();
        setShowProcessModal(false);
        setProcessItem(null);
      } else {
        toast.error(response.message || 'Süreç ilerletilemedi');
      }
    } catch (error) {
      console.error('Error advancing process:', error);
      toast.error('Süreç ilerletilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const openAdvanceProcess = (item: SupplyRecord) => {
    if (!canAdvanceProcess(item.islemDurum as ProcessStatus)) {
      toast.error('Süreç zaten son aşamada');
      return;
    }
    setProcessItem(item);
    setShowProcessModal(true);
  };

  const openDetailModal = (item: SupplyRecord) => {
    setDetailTab('general');
    setDetailItem(item);
  };

  const openEditFromDetail = () => {
    if (!detailItem) return;
    setEditingItem(detailItem);
    setDetailItem(null);
    setIsFormOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100dvh-4rem-var(--safe-area-top))] flex items-center justify-center px-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100dvh-4rem-var(--safe-area-top))] min-w-0 flex-col gap-4">
      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Tab'ler */}
        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <button
              onClick={() => setSupplyTab('all')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                supplyTab === 'all'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'border border-transparent text-slate-600 hover:bg-white hover:border-slate-200'
               }`}
            >
              Tümü ({data.length})
            </button>
            <button
              onClick={() => setSupplyTab('tedarik-bekliyor')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                supplyTab === 'tedarik-bekliyor'
                  ? 'bg-pink-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Tedarik Bekliyor ({data.filter((d) => {
                const hasAssignedDriver = Boolean(String(d.surucuBilgileri || d.surucuAd || '').trim());
                const hasAssignedVehicle = Boolean(String(d.cekici || d.aracPlaka || '').trim());
                return d.durum === 'tedarik-bekleniyor' || d.durum === 'tedarik-bekliyor' || (!hasAssignedDriver && !hasAssignedVehicle);
              }).length})
            </button>
            <button
              onClick={() => setSupplyTab('teklif-bekliyor')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                supplyTab === 'teklif-bekliyor'
                  ? 'bg-violet-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Teklif Bekliyor ({activeBidRequestShipmentIds.length})
            </button>
            <button
              onClick={() => setSupplyTab('filo-tedarik')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                supplyTab === 'filo-tedarik'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Filo Tedarik ({data.filter(d => d.durum === 'filo-tedarik-edildi').length})
            </button>
            <button
              onClick={() => setSupplyTab('spot-tedarik')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                supplyTab === 'spot-tedarik'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Spot Tedarik ({data.filter(d => d.durum === 'spot-tedarik-edildi').length})
            </button>
          </div>
        </div>

        <div className="px-4 pt-4 sm:px-6 sm:pt-6">
          <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-6 py-6 text-white shadow-sm">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-blue-100 ring-1 ring-white/10">
                  Operasyon Paneli
                </div>
                <h1 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">Tedarik Yönetimi</h1>
                <p className="mt-2 text-sm text-slate-200">
                  {activeTabLabel} ekranındasınız. Tedarik, yükleme ve boşaltma süreçlerini tek merkezden takip edin.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:min-w-[420px] xl:grid-cols-4">
                <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/10">
                  <div className="text-xs text-slate-300">Toplam</div>
                  <div className="mt-1 text-2xl font-semibold">{stats.total}</div>
                </div>
                <div className="rounded-2xl bg-amber-400/10 px-4 py-3 ring-1 ring-amber-300/20">
                  <div className="text-xs text-amber-100">Bekleyen</div>
                  <div className="mt-1 text-2xl font-semibold text-amber-200">{stats.waiting}</div>
                </div>
                <div className="rounded-2xl bg-blue-400/10 px-4 py-3 ring-1 ring-blue-300/20">
                  <div className="text-xs text-blue-100">Süreçte</div>
                  <div className="mt-1 text-2xl font-semibold text-blue-200">{stats.inProgress}</div>
                </div>
                <div className="rounded-2xl bg-emerald-400/10 px-4 py-3 ring-1 ring-emerald-300/20">
                  <div className="text-xs text-emerald-100">Tamamlanan</div>
                  <div className="mt-1 text-2xl font-semibold text-emerald-200">{stats.completed}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-4 px-4 py-4 sm:flex-row sm:items-center sm:px-6 sm:py-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Araçlar ve İşlemler</h2>
            <p className="text-sm text-slate-500 mt-1">Veri çekme, kayıt üretme ve toplu aksiyonları buradan yönetin.</p>
          </div>
          
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
            {selectedItems.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowBulkMenu((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  <MoreVertical className="w-4 h-4" />
                  Seçilenler ({selectedItems.length})
                </button>
                {showBulkMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                    <button onClick={handleBulkOfferRequest} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left text-purple-700 hover:bg-purple-50">
                      <Send className="w-4 h-4" /> Teklif Topla
                    </button>
                    <button onClick={handleBulkShare} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left text-blue-700 hover:bg-blue-50">
                      <Share2 className="w-4 h-4" /> Paylaş
                    </button>
                    <button onClick={handleBulkWhatsappShare} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left text-green-700 hover:bg-green-50">
                      <MessageCircle className="w-4 h-4" /> WhatsApp'ta Paylaş
                    </button>
                    <button onClick={handleBulkDelete} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-left text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" /> Sil
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={loadData}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Yenile"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid Görünümü"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Kart Görünümü"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowColumnPicker((prev) => !prev)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Columns3 className="w-4 h-4" />
                Kolonlar
              </button>

              {showColumnPicker && (
                <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-30 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-700">Görünecek Alanlar</div>
                    <button
                      onClick={() => setVisibleColumnKeys(allColumns.filter((c) => c.key !== 'id').map((c) => String(c.key)))}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Tümünü Seç
                    </button>
                  </div>

                  <div className="space-y-1">
                    {allColumns
                      .filter((column) => column.key !== 'id')
                      .map((column) => {
                        const key = String(column.key);
                        const checked = visibleColumnKeys.includes(key);
                        return (
                          <label key={key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setVisibleColumnKeys((prev) => [...new Set([...prev, key])]);
                                } else {
                                  setVisibleColumnKeys((prev) => prev.filter((k) => k !== key));
                                }
                              }}
                            />
                            <span>{column.label}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {hasFeaturePermission('canCreateSupply') && (
              <button
                onClick={async () => {
                  try {
                    const response: any = await customerService.syncFromShipments();
                    toast.success(response.message || 'Sevkiyatlardan müşteriler işlendi');
                    await loadCustomerLookup();
                  } catch (error) {
                    console.error('Sync customers from shipments failed:', error);
                    toast.error('Sevkiyatlardan müşteri üretilemedi');
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Noktalardan Müşteri Üret</span>
                <span className="sm:hidden">Müşteri Üret</span>
              </button>
            )}

            {hasFeaturePermission('canCreateSupply') && (
              <button
                onClick={async () => {
                  if (!confirm('Sevkiyatlardan otomatik üretilen müşteri kayıtları silinsin mi?')) return;
                  try {
                    const response: any = await customerService.deleteGeneratedFromShipments();
                    toast.success(response.message || 'Üretilen müşteriler silindi');
                    await loadCustomerLookup();
                  } catch (error) {
                    console.error('Delete generated shipment customers failed:', error);
                    toast.error('Üretilen müşteriler silinemedi');
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white text-sm rounded-lg hover:bg-rose-700 transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Üretilen Müşterileri Sil</span>
                <span className="sm:hidden">Müşteri Sil</span>
              </button>
            )}

            {hasFeaturePermission('canCreateSupply') && (
              <button
                onClick={() => { setQuickPrompt(''); setQuickPreview(null); setShowQuickWizard(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Hızlı Tedarik Oluştur</span>
                <span className="sm:hidden">Hızlı</span>
              </button>
            )}

            {hasFeaturePermission('canCreateSupply') && (
              <button 
                onClick={() => {
                  setEditingItem(null);
                  setQuickInitialData(null);
                  setIsFormOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Kayıt Ekle</span>
                <span className="sm:hidden">Ekle</span>
              </button>
            )}
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Akıllı Filtreler</h3>
                <p className="text-xs text-slate-500 mt-1">Listeyi yükleme noktası, tarih ve araç durumuna göre daraltın.</p>
              </div>
              <button
                type="button"
                onClick={() => { setLoadingPointFilter(''); setDateFilter(''); setVehicleStatusFilter(''); }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
              >
                Filtreleri Temizle
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yükleme Noktasına Göre Filtrele</label>
            <input
              value={loadingPointFilter}
              onChange={(e) => setLoadingPointFilter(e.target.value)}
              placeholder="Yükleme müşteri adı / il / ilçe"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tarihe Göre Filtrele</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Araç Durum</label>
            <select
              value={vehicleStatusFilter}
              onChange={(e) => setVehicleStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Tümü</option>
              <option value="YÜK VERİLDİ">YÜK VERİLDİ</option>
              <option value="ARAÇ BOŞ">ARAÇ BOŞ</option>
              <option value="ARACIM BOŞ">ARACIM BOŞ</option>
              <option value="YOLDA">YOLDA</option>
              <option value="TAKO MOLASI">TAKO MOLASI</option>
              <option value="İZİNDE">İZİNDE</option>
              <option value="ARIZALI">ARIZALI</option>
              <option value="RAPORLU">RAPORLU</option>
              <option value="YÜKLEME BEKLİYOR">YÜKLEME BEKLİYOR</option>
              <option value="YÜKLEMEDE">YÜKLEMEDE</option>
              <option value="TAHLİYE BEKLİYOR">TAHLİYE BEKLİYOR</option>
            </select>
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="h-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {filteredSupplyData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center">
              <Package className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Gösterilecek kayıt bulunamadı</h3>
              <p className="mt-2 max-w-md text-sm text-slate-500">
                Mevcut filtreler seçili görünüm için sonuç üretmedi. Filtreleri temizleyerek veya farklı bir süreç sekmesi seçerek devam edin.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <AdvancedTable 
              data={filteredSupplyData} 
              columns={columns}
              onRefresh={loadData}
              selectedRows={selectedItems}
              onSelectedRowsChange={setSelectedItems}
              fixedFirstColumn
              onRowDoubleClick={openDetailModal}
            />
          ) : (
            <SupplyCardsView 
              data={filteredSupplyData} 
              onEdit={(item) => {
                setEditingItem(item);
                setIsFormOpen(true);
              }}
            />
          )}
        </div>
      </div>

      {/* Form Modal */}
      <SupplyFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          if (searchParams.get('shipment')) {
            const next = new URLSearchParams(searchParams);
            next.delete('shipment');
            setSearchParams(next, { replace: true });
          }
        }}
        editingItem={editingItem}
        initialData={quickInitialData}
        onSave={handleSave}
      />

      {showQuickWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Hızlı Tedarik Oluştur</h3>
                <p className="text-sm text-gray-500 mt-1">Serbest metin yazın, sistem temel alanları doldursun.</p>
              </div>
              <button onClick={() => setShowQuickWizard(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <textarea
                value={quickPrompt}
                onChange={(e) => setQuickPrompt(e.target.value)}
                className="w-full min-h-48 rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Örn: Yükleme [TESIS ADI] [IL], boşaltma [MUSTERI ADI] [IL], araç türü Tır Kapalı Dorse, palet 32, tonaj 5000, navlun 39000, telefon +905XX..., sipariş A04-194"
              />
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                Yazarken şu alanları doğal dille verebilirsin: yükleme, boşaltma, hedef müşteri, yükleme tarihi, yük türü, tonaj, palet, navlun, telefon, durak sayısı.
              </div>

              {quickPreview && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-sm font-semibold text-emerald-900 mb-3">Önizleme Alanları</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-emerald-800 font-medium">Yükleme:</span> {quickPreview.yuklemeMusterisiAd || '-'} / {quickPreview.yuklemeIl || '-'} {quickPreview.yuklemeIlce || ''}</div>
                    <div><span className="text-emerald-800 font-medium">Boşaltma:</span> {quickPreview.teslimMusterisiAd || '-'} / {quickPreview.teslimIl || '-'} {quickPreview.teslimIlce || ''}</div>
                    <div><span className="text-emerald-800 font-medium">Yükleme Tarihi:</span> {quickPreview.yuklemeTalepTarihi || '-'}</div>
                    <div><span className="text-emerald-800 font-medium">Yük Türü:</span> {quickPreview.yukTipi || '-'}</div>
                    <div><span className="text-emerald-800 font-medium">Yük Miktarı:</span> {quickPreview.yukAgirlik || 0}</div>
                    <div><span className="text-emerald-800 font-medium">Palet:</span> {quickPreview.palletCount || 0}</div>
                    <div><span className="text-emerald-800 font-medium">Durak Sayısı:</span> {quickPreview.stopCount || 1}</div>
                    <div><span className="text-emerald-800 font-medium">Araç Türü:</span> {quickPreview.aracTuru || '-'}</div>
                    <div><span className="text-emerald-800 font-medium">Sipariş:</span> {quickPreview.siparisKodu || '-'}</div>
                    <div><span className="text-emerald-800 font-medium">Navlun:</span> {quickPreview.navlun || 0}</div>
                    <div><span className="text-emerald-800 font-medium">Telefon:</span> {quickPreview.telefon || '-'}</div>
                    {Array.isArray(quickPreview.cokluBosaltmaHedefleri) && quickPreview.cokluBosaltmaHedefleri.length > 1 && (
                      <div className="md:col-span-2"><span className="text-emerald-800 font-medium">Çoklu Boşaltma Hedefleri:</span> {quickPreview.cokluBosaltmaHedefleri.join(' | ')}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => { setShowQuickWizard(false); setQuickPreview(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Vazgeç</button>
              <button onClick={handleQuickWizardPreview} className="px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50">Önizle</button>
              <button onClick={handleQuickWizardApply} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Forma Aktar</button>
            </div>
          </div>
        </div>
      )}

      {imageGallery && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{imageGallery.title}</h3>
                  <p className="text-xs text-gray-500">{imageGallery.selectedIndex + 1} / {imageGallery.documents.length}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setImageGallery(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                aria-label="Galeriyi kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 flex-1 overflow-auto">
              <div className="w-full bg-gray-100 rounded-xl flex items-center justify-center min-h-[320px]">
                <img
                  src={imageGallery.documents[imageGallery.selectedIndex]?.url}
                  alt={imageGallery.documents[imageGallery.selectedIndex]?.name}
                  className="max-h-[60vh] w-auto object-contain rounded-lg"
                />
              </div>

              <div className="mt-4 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  {imageGallery.documents.map((doc, index) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => setImageGallery((prev) => prev ? { ...prev, selectedIndex: index } : prev)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${index === imageGallery.selectedIndex ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}`}
                      title={doc.name}
                    >
                      <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Card View Component
function SupplyCardsView({ data, onEdit }: { data: SupplyRecord[]; onEdit: (item: SupplyRecord) => void }) {
  const StatusBadge = ({ status }: { status: SupplyStatus }) => {
    const config = SUPPLY_STATUS_CONFIG[status];
    if (!config) {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
          {status || 'Bilinmeyen'}
        </span>
      );
    }
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium"
        style={{ backgroundColor: config.bgColor, color: config.color }}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.map((item) => (
          <div
            key={item.id}
            onClick={() => onEdit(item)}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-lg font-bold text-gray-900">#{item.sevkKodu}</div>
                {item.planKodu && <div className="mt-1 text-xs font-semibold text-blue-600">Plan: {item.planKodu}</div>}
                <div className="text-xs text-gray-500 mt-1">{item.yuklemeTalepTarihi}</div>
              </div>
              <StatusBadge status={item.durum} />
            </div>

            {/* Details */}
            <div className="space-y-2">
              {item.siparisKodu && (
                <div className="flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600 truncate">{item.siparisKodu}</span>
                </div>
              )}

              {item.hizliArama && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600 truncate">{item.hizliArama}</span>
                </div>
              )}

              {item.yuklemeMusterisiAd && (
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600 truncate">{item.yuklemeMusterisiAd}</span>
                </div>
              )}

              {item.yuklemeIl && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">
                    {item.yuklemeIl}
                    {item.yuklemeIlce && ` / ${item.yuklemeIlce}`}
                  </span>
                </div>
              )}

              {Number(item.maliyet || 0) > 0 && (
                <div className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 border border-emerald-200 mt-2">
                  Baz Fiyat: ₺{Number(item.maliyet).toLocaleString('tr-TR')}
                </div>
              )}

              {item.aracAtamaTarihi && (
                <div className="flex items-center gap-2 text-sm mt-3 pt-3 border-t border-gray-100">
                  <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{item.aracAtamaTarihi}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <Package className="w-16 h-16 mb-4 opacity-50" />
          <p>Henüz tedarik kaydı bulunmuyor</p>
        </div>
      )}

      {/* Süreç İlerlet Modal */}
      {showProcessModal && processItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">Süreci İlerlet</h3>
              <p className="text-sm text-gray-600 mt-1">#{processItem.sevkKodu}</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Mevcut Durum:</span>
                <span className="font-medium text-gray-900">{PROCESS_STATUS_CONFIG[processItem.islemDurum as ProcessStatus]?.label || processItem.islemDurum || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Sonraki:</span>
                <span className="font-medium text-green-600">{getNextProcessStatus(processItem.islemDurum as ProcessStatus) ? PROCESS_STATUS_CONFIG[getNextProcessStatus(processItem.islemDurum as ProcessStatus)!]?.label : '-'}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowProcessModal(false); setProcessItem(null); }}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
              >
                Vazgeç
              </button>
              <button
                onClick={() => handleAdvanceProcess(processItem)}
                disabled={saving}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ArrowRight className="w-5 h-5" /> İlerlet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailItem && (
        <div className="fixed inset-0 z-[60] bg-black/45 p-4 flex items-center justify-center">
          <div className="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-[#f8fafc] shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <div className="text-sm text-slate-500">Operasyon Yönetimi &gt; Tedarik Yönetimi &gt; Detay</div>
                <h2 className="mt-3 text-[48px] font-bold leading-none text-slate-500">{detailItem.sevkKodu || 'SEVK KODU'}</h2>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {SUPPLY_STATUS_CONFIG[detailItem.durum] ? <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: SUPPLY_STATUS_CONFIG[detailItem.durum].bgColor, color: SUPPLY_STATUS_CONFIG[detailItem.durum].color }}>{SUPPLY_STATUS_CONFIG[detailItem.durum].label}</span> : null}
                  {detailItem.islemDurum ? processStatusBadge(detailItem.islemDurum) : null}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={openEditFromDetail} className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">Düzenle</button>
                <button onClick={() => setDetailItem(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white px-6">
              <div className="flex items-center gap-8 overflow-x-auto">
                {[
                  { key: 'general', label: 'Yük Bilgileri' },
                  { key: 'assignment', label: 'Araç Bilgileri' },
                  { key: 'pricing', label: 'Finansal Özet' },
                  { key: 'documents', label: 'Notlar & Süreç' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setDetailTab(tab.key as typeof detailTab)}
                    className={`whitespace-nowrap border-b-2 px-1 py-4 text-[15px] font-semibold transition-colors ${detailTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-700 hover:text-slate-900'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {detailTab === 'general' && (
                <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-6"><h3 className="text-[18px] font-semibold text-slate-900">Yük bilgileri</h3></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Yükleme Noktası</div><div className="mt-2 text-base font-semibold text-slate-900">{detailItem.yuklemeMusterisiAd || '-'}</div><div className="mt-1 text-sm text-slate-600">{detailItem.yuklemeIl || '-'}{detailItem.yuklemeIlce ? ` / ${detailItem.yuklemeIlce}` : ''}</div><div className="mt-2 text-sm text-slate-500">{detailItem.yuklemeAdres || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Yükleme Tarihi</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.yuklemeTalepTarihi ? new Date(detailItem.yuklemeTalepTarihi).toLocaleString('tr-TR') : '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Yük Tipi</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.yukTipi || '-'}</div></div>
                    </div>
                    <div className="space-y-5">
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Teslim Noktası</div><div className="mt-2 text-base font-semibold text-slate-900">{detailItem.teslimMusterisiAd || '-'}</div><div className="mt-1 text-sm text-slate-600">{detailItem.teslimIl || '-'}{detailItem.teslimIlce ? ` / ${detailItem.teslimIlce}` : ''}</div><div className="mt-2 text-sm text-slate-500">{detailItem.teslimAdres || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">İstenen Teslim Tarihi</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.istenenTeslimTarihi ? new Date(detailItem.istenenTeslimTarihi).toLocaleString('tr-TR') : '-'}</div></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs text-slate-500">Ağırlık</div><div className="mt-1 text-sm font-semibold text-slate-900">{Number(detailItem.yukAgirlik || 0).toLocaleString('tr-TR')} kg</div></div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs text-slate-500">Mesafe</div><div className="mt-1 text-sm font-semibold text-slate-900">{detailItem.mesafe || 0} km</div></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {detailTab === 'assignment' && (
                <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-6"><h3 className="text-[18px] font-semibold text-slate-900">Araç bilgileri</h3></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Sürücü</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.surucuBilgileri || detailItem.surucuAd || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.telefon || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Araç Durumu</div><div className="mt-2">{vehicleStatusBadge(detailItem.aracDurum || '-')}</div></div>
                    </div>
                    <div className="space-y-5">
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Araç Tipi</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.aracTipi || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Çekici / Dorse</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.cekici || '-'}{detailItem.dorsePlaka ? ` / ${detailItem.dorsePlaka}` : ''}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Araç Atama Tarihi</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.aracAtamaTarihi ? new Date(detailItem.aracAtamaTarihi).toLocaleString('tr-TR') : '-'}</div></div>
                    </div>
                  </div>
                </div>
              )}

              {detailTab === 'pricing' && (
                <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-6"><h3 className="text-[18px] font-semibold text-slate-900">Fiyat Giriş</h3></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs text-slate-500">Baz Fiyat</div><div className="mt-1 text-lg font-semibold text-slate-900">₺{Number(detailItem.maliyet || 0).toLocaleString('tr-TR')}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs text-slate-500">Navlun</div><div className="mt-1 text-lg font-semibold text-slate-900">₺{Number(detailItem.navlun || 0).toLocaleString('tr-TR')}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs text-slate-500">Komisyon</div><div className="mt-1 text-lg font-semibold text-slate-900">₺{Number(detailItem.komisyon || 0).toLocaleString('tr-TR')}</div></div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><div className="text-xs text-emerald-700">Genel Toplam</div><div className="mt-1 text-lg font-semibold text-emerald-800">₺{Number(detailItem.genelToplam || 0).toLocaleString('tr-TR')}</div></div>
                  </div>
                  <div className="mt-6 flex items-center gap-4 border-t border-slate-200 pt-4">
                    <span className="text-sm font-medium text-slate-700">Hammaliye Bedeli</span>
                    <button
                      type="button"
                      disabled={hammaliyeLoading}
                      onClick={async () => {
                        try {
                          setHammaliyeLoading(true);
                          const newVal = detailItem.hammaliyeBedeli > 0 ? 0 : 1;
                          const resp = await supplyService.update(detailItem.id, { hammaliyeBedeli: newVal });
                          if (resp?.success) {
                            setDetailItem((prev) => prev ? { ...prev, hammaliyeBedeli: newVal } : prev);
                            loadData();
                          }
                        } catch (err) {
                          console.error('Hammaliye guncelleme hatasi:', err);
                        } finally {
                          setHammaliyeLoading(false);
                        }
                      }}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${detailItem.hammaliyeBedeli > 0 ? 'bg-emerald-500' : 'bg-slate-300'} ${hammaliyeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${detailItem.hammaliyeBedeli > 0 ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-semibold ${detailItem.hammaliyeBedeli > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                      {detailItem.hammaliyeBedeli > 0 ? 'Eklendi' : 'Eklenmedi'}
                    </span>
                  </div>
                </div>
              )}

              {detailTab === 'documents' && (
                <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="mb-6"><h3 className="text-[18px] font-semibold text-slate-900">Notlar ve Süreç</h3></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Tedarik Durumu</div><div className="mt-2 inline-flex">{SUPPLY_STATUS_CONFIG[detailItem.durum] ? <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: SUPPLY_STATUS_CONFIG[detailItem.durum].bgColor, color: SUPPLY_STATUS_CONFIG[detailItem.durum].color }}>{SUPPLY_STATUS_CONFIG[detailItem.durum].label}</span> : detailItem.durum}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">İşlem Durumu</div><div className="mt-2 inline-flex">{processStatusBadge(detailItem.islemDurum || '-')}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Talep Oluşturan</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.talepOlusturan || '-'}</div></div>
                      <div><div className="text-xs font-medium uppercase tracking-wide text-slate-500">Operasyon Takip</div><div className="mt-2 text-sm font-semibold text-slate-900">{detailItem.operasyonTakipKod || '-'}</div></div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Notlar</div>
                      <div className="mt-2 min-h-[220px] rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap">
                        {detailItem.notlar || detailItem.aciklama || 'Bu kayıt için not bulunmuyor.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function useAuditLog() {
  return {
    logAction: {
      offerRequestCreated: (id: string | number, code: string) => {
        console.log('Offer request created:', id, code);
      }
    }
  };
}
